import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import MaximizeIcon from '@atlaskit/icon/core/maximize';
import CrossIcon from '@atlaskit/icon/core/cross';
import Tooltip from '@atlaskit/tooltip';

import { useAudioBriefing } from '../../hooks/useAudioBriefing';
import { AudioTimeIndicator } from '../controls/AudioTimeIndicator';
import { PlayPauseButton } from '../controls/PlayPauseButton';

const i18n = defineMessages({
	closePopupLabel: {
		id: 'audio.audio-popup.close-minimized-player',
		defaultMessage: 'Close popup',
		description: 'label for the close button in the minimized player',
	},
	closePopupTooltip: {
		id: 'audio.audio-popup.close-minimized-player-tooltip',
		defaultMessage: 'Close',
		description: 'tooltip for the close button in the minimized player',
	},
	maximizePopupLabel: {
		id: 'audio.audio-popup.maximize-minimized-player',
		defaultMessage: 'Expand popup',
		description: 'label for the maximize button in the minimized player',
	},
	maximizePopupTooltip: {
		id: 'audio.audio-popup.maximize-minimized-player-tooltip',
		defaultMessage: 'Expand',
		description: 'tooltip for the maximize button in the minimized player',
	},
});

const styles = cssMap({
	container: {
		width: '280px',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		padding: token('space.100'),
	},
});

type MinimizedAudioPlayerProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	onClose: () => void;
	onMaximize: () => void;
	fetchContent: () => void;
	prefetchLabel?: string;
	loadingLabel?: string;
};

export const MinimizedAudioPlayer = ({
	audioRef,
	onClose,
	onMaximize,
	fetchContent,
	prefetchLabel,
	loadingLabel,
}: MinimizedAudioPlayerProps) => {
	const [{ activeContent, isPlaying, isLoading, hasFetched }] = useAudioBriefing();

	const { formatMessage } = useIntl();

	const contentTitle = activeContent?.contentTitle;

	return (
		<Box xcss={styles.container} testId="minimized-audio-player">
			<Inline spread="space-between" alignBlock="center" space="space.100">
				<Inline space="space.100" alignBlock="center">
					<PlayPauseButton
						audioRef={audioRef}
						isPlaying={isPlaying}
						isLoading={isLoading}
						hasFetched={hasFetched}
						fetchContent={fetchContent}
					/>
					<Stack alignInline="start">
						<Text weight="medium" maxLines={1}>
							{contentTitle}
						</Text>
						<AudioTimeIndicator
							audioRef={audioRef}
							isLoading={isLoading}
							hasFetched={hasFetched}
							prefetchLabel={prefetchLabel}
							loadingLabel={loadingLabel}
						/>
					</Stack>
				</Inline>
				<Inline space="space.050">
					<Tooltip content={formatMessage(i18n.maximizePopupTooltip)}>
						<IconButton
							icon={MaximizeIcon}
							label={formatMessage(i18n.maximizePopupLabel)}
							appearance="subtle"
							shape="circle"
							onClick={onMaximize}
							testId="audio-popup-maximize-button"
						/>
					</Tooltip>
					<Tooltip content={formatMessage(i18n.closePopupTooltip)}>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.closePopupLabel)}
							appearance="subtle"
							shape="circle"
							onClick={onClose}
							testId="audio-popup-close-button"
						/>
					</Tooltip>
				</Inline>
			</Inline>
		</Box>
	);
};
